import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Check,
  ClipboardCheck,
  Code2,
  DatabaseZap,
  FileCheck2,
  Fingerprint,
  Gauge,
  KeyRound,
  Lock,
  Mail,
  Phone,
  PlugZap,
  ScrollText,
  Search,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Terminal,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import EmailGate from "../components/EmailGate";
import { getStoredContentSessionId } from "../lib/gatedContentTracking";

const slides = [
  { slug: "open", title: "The Program Is Open", section: "Overview" },
  { slug: "access-model", title: "The Access Model", section: "Access" },
  { slug: "read-access", title: "Read Access", section: "Access" },
  { slug: "write-access", title: "Write Access", section: "Access" },
  { slug: "security", title: "Security Measures", section: "Security" },
  { slug: "security-controls", title: "Security Controls", section: "Security" },
  { slug: "getting-access", title: "Getting Access", section: "Start" },
  { slug: "apply", title: "Apply", section: "Start" },
];

// Hero endpoint preview — shows the shape of the access model at a glance.
const endpointRows = [
  { method: "GET", path: "/v1/patients", tag: "read", state: "ok" },
  { method: "GET", path: "/v1/appointments", tag: "read", state: "ok" },
  { method: "GET", path: "/v1/claims", tag: "read", state: "ok" },
  { method: "GET", path: "/v1/clinical/*", tag: "read", state: "ok" },
  { method: "POST", path: "/v1/patients", tag: "write", state: "ok" },
  { method: "POST", path: "/v1/appointments", tag: "write", state: "ok" },
  { method: "POST", path: "/v1/{ more }", tag: "on request", state: "more" },
  { method: "MCP", path: "/mcp/tools", tag: "included", state: "included" },
];

const programPillars = [
  "Open to developers building on top of the Asha dental OS.",
  "Read access across most of the data model.",
  "Write access to patients and appointments — more on request.",
  "MCP is part of the same API — no separate request.",
  "Quote-based pricing, granted through application + security review.",
];

const readFieldGroups = [
  {
    title: "Patients & households",
    fields: [
      "patient_id",
      "firstname",
      "lastname",
      "birthdate",
      "chart_number",
      "email",
      "cell",
      "address",
      "guarantor_id",
      "subscriber_id",
      "family mapping",
    ],
  },
  {
    title: "Scheduling & capacity",
    fields: [
      "appointment_id",
      "date",
      "time",
      "length",
      "status",
      "provider_id",
      "operatory",
      "scheduled_code",
      "available slots",
      "cancelled date",
      "resource_id",
    ],
  },
  {
    title: "Insurance & eligibility",
    fields: [
      "insurance_company_id",
      "payer id",
      "plan coverage",
      "percentage_covered",
      "deductible",
      "co_pay",
      "eligibility status",
      "group number",
      "benefits",
      "estimated insurance",
    ],
  },
  {
    title: "Claims & RCM",
    fields: [
      "claim_id",
      "claim status",
      "claim_sent_date",
      "claim_payment_date",
      "transaction_id",
      "account balance",
      "amount_0_30",
      "amount_31_60",
      "amount_90_plus",
      "unapplied payments",
    ],
  },
  {
    title: "Clinical & treatment",
    fields: [
      "procedure_code",
      "treatment_plan_id",
      "treatment phase",
      "tooth_number",
      "surface",
      "perio pocket_depth",
      "medical note",
      "xray link",
      "prescription",
      "lab case",
    ],
  },
  {
    title: "Practice intelligence",
    fields: [
      "practice_id",
      "provider mapping",
      "fee schedule",
      "procedure categories",
      "sync status",
      "data freshness",
      "KPI definitions",
      "payment types",
      "referrals",
      "documents",
    ],
  },
];

const writeScopedGroups = [
  {
    title: "Patients",
    endpoints: ["POST /v1/patients", "PATCH /v1/patients/{id}"],
    fields: [
      "firstname",
      "lastname",
      "birthdate",
      "gender",
      "address",
      "city",
      "state",
      "email",
      "cell",
      "homephone",
      "language",
      "guarantor",
    ],
  },
  {
    title: "Appointments",
    endpoints: [
      "POST /v1/appointments",
      "PUT /v1/appointments/{id}",
      "PATCH /v1/appointments/{id}",
    ],
    fields: [
      "patient_id",
      "date",
      "time",
      "length",
      "provider_id",
      "operatory",
      "status",
      "type",
      "description",
      "cancellation_note",
      "staff",
    ],
  },
];

const moreOnRequest = [
  "claims",
  "payments",
  "documents",
  "clinical notes",
  "procedures",
  "perio charting",
  "treatment plans",
  "ledger",
];

const securityMeasures = [
  {
    icon: ShieldCheck,
    title: "BAA + HIPAA",
    body: "We sign a Business Associate Agreement. All PHI is handled under HIPAA controls.",
  },
  {
    icon: KeyRound,
    title: "OAuth 2.0 + scoped keys",
    body: "Per-integration credentials. Tokens carry only the scopes your use case needs.",
  },
  {
    icon: Fingerprint,
    title: "Least-privilege by default",
    body: "Read is broad, write is narrow. Nothing is enabled beyond what we explicitly grant.",
  },
  {
    icon: Lock,
    title: "Encrypted end to end",
    body: "TLS 1.2+ in transit and encryption at rest across the platform.",
  },
  {
    icon: Gauge,
    title: "Rate limits & quotas",
    body: "Per-key throttling and quotas protect the platform and every practice on it.",
  },
  {
    icon: ScrollText,
    title: "Audit logging & revocation",
    body: "Every read and write is logged and attributable. Access can be scoped down or revoked.",
  },
];

// Representative HIPAA controls — the kind a vendor passing a Drata/Vanta
// program would have. Surfaced as examples on the deck; the full list is
// shared with applicants.
const securityControls = [
  "SOC 2 Type II program, continuously monitored",
  "HIPAA Security & Privacy Rule controls",
  "BAAs executed with all subprocessors",
  "MFA enforced on all production & admin access",
  "Role-based access control, least privilege",
  "Quarterly user access reviews",
  "Encryption at rest (AES-256)",
  "Encryption in transit (TLS 1.2+)",
  "Centralized, immutable audit logging",
  "PHI access trails — who, what, when",
  "Annual third-party penetration testing",
  "Continuous vulnerability scanning & patch SLAs",
  "Endpoint protection & MDM on all devices",
  "Personnel background checks",
  "Annual security & HIPAA awareness training",
  "Documented incident response & breach notification",
  "Business continuity & disaster recovery, tested",
  "Encrypted backups with point-in-time recovery",
  "Change management with mandatory code review",
  "Vendor / subprocessor risk reviews",
  "Network segmentation, firewalls & WAF",
];

const accessSteps = [
  {
    icon: ClipboardCheck,
    title: "Apply to the program",
    body: "Submit the developer application with your use case and the endpoints you need.",
  },
  {
    icon: ShieldCheck,
    title: "Complete the security questionnaire",
    body: "Provide a 3rd-party audit certifying your tech stack is HIPAA compliant.",
  },
  {
    icon: ServerCog,
    title: "We scope access & send a quote",
    body: "We set least-privilege scopes for your use case — including MCP — and pricing is quote-based.",
  },
  {
    icon: BadgeCheck,
    title: "Go live",
    body: "Receive scoped credentials. Read, write, and MCP, all under your agreement.",
  },
];

const whatToHaveReady = [
  { icon: Code2, text: "Your use case and the endpoints you need" },
  { icon: ServerCog, text: "Your tech stack & where PHI is processed" },
  { icon: ShieldCheck, text: "A 3rd-party audit certifying HIPAA compliance" },
];

const cn = (...classes) => classes.filter(Boolean).join(" ");

function SlideShell({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  titleAccent,
  subtitle,
  children,
  dark = false,
  tone = "blue",
}) {
  const toneMap = {
    blue: {
      lightBadge: "bg-blue-50 text-blue-600 border-blue-100/60",
      darkBadge: "bg-blue-500/10 text-blue-300 border-blue-500/20",
      lightAccent: "from-blue-600 to-indigo-600",
      darkAccent: "from-blue-400 to-indigo-400",
      lightOrbA: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)",
      lightOrbB: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)",
    },
    emerald: {
      lightBadge: "bg-emerald-50 text-emerald-600 border-emerald-100/60",
      darkBadge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      lightAccent: "from-emerald-600 to-teal-600",
      darkAccent: "from-emerald-400 to-teal-400",
      lightOrbA: "linear-gradient(135deg, #6ee7b7 0%, #93c5fd 50%, #c4b5fd 100%)",
      lightOrbB: "linear-gradient(135deg, #a7f3d0 0%, #67e8f9 50%, #93c5fd 100%)",
    },
    indigo: {
      lightBadge: "bg-indigo-50 text-indigo-600 border-indigo-100/60",
      darkBadge: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20",
      lightAccent: "from-indigo-600 to-blue-600",
      darkAccent: "from-indigo-400 to-blue-400",
      lightOrbA: "linear-gradient(135deg, #c7d2fe 0%, #93c5fd 50%, #bfdbfe 100%)",
      lightOrbB: "linear-gradient(135deg, #ddd6fe 0%, #a5b4fc 50%, #bfdbfe 100%)",
    },
  };
  const t = toneMap[tone] || toneMap.blue;

  return (
    <section
      className={cn(
        "relative h-full w-full overflow-hidden p-10 xl:p-12 font-sans",
        dark ? "bg-[#0B1120] text-white" : "bg-[#f8fafc] text-slate-900"
      )}
    >
      {dark ? (
        <>
          <div className="pointer-events-none absolute top-0 left-0 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/10 blur-[120px]" />
          <div className="pointer-events-none absolute bottom-0 right-0 h-[800px] w-[800px] translate-x-1/2 translate-y-1/2 rounded-full bg-indigo-600/10 blur-[120px]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:48px_48px]" />
        </>
      ) : (
        <>
          <div
            className="pointer-events-none absolute -top-[200px] -right-[120px] h-[700px] w-[700px] rounded-full opacity-30 blur-[120px]"
            style={{ background: t.lightOrbA }}
          />
          <div
            className="pointer-events-none absolute -bottom-[200px] -left-[120px] h-[600px] w-[600px] rounded-full opacity-25 blur-[120px]"
            style={{ background: t.lightOrbB }}
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40" />
        </>
      )}

      <div className="relative z-10 flex h-full flex-col">
        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7 max-w-5xl"
        >
          {eyebrow && (
            <div
              className={cn(
                "mb-3 inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[13px] font-semibold backdrop-blur-md",
                dark ? t.darkBadge : t.lightBadge
              )}
            >
              {EyebrowIcon && <EyebrowIcon className="h-3.5 w-3.5" />}
              <span className="tracking-wide">{eyebrow}</span>
            </div>
          )}
          <h1
            className={cn(
              "max-w-5xl text-[40px] font-bold leading-[1.05] tracking-tight",
              dark ? "text-white" : "text-slate-900"
            )}
          >
            {title}
            {titleAccent && (
              <>
                {" "}
                <span
                  className={cn(
                    "bg-gradient-to-r bg-clip-text text-transparent",
                    dark ? t.darkAccent : t.lightAccent
                  )}
                >
                  {titleAccent}
                </span>
              </>
            )}
          </h1>
          {subtitle && (
            <p
              className={cn(
                "mt-3 max-w-4xl text-[17px] leading-relaxed",
                dark ? "text-slate-300" : "text-slate-600"
              )}
            >
              {subtitle}
            </p>
          )}
        </motion.header>
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </section>
  );
}

function MetricPill({ label, value, tone = "blue", dark = false }) {
  const tones = dark
    ? {
        blue: "border-blue-500/25 bg-blue-500/10 text-blue-200",
        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
        indigo: "border-indigo-500/25 bg-indigo-500/10 text-indigo-200",
      }
    : {
        blue: "border-blue-200/70 bg-white text-blue-700",
        emerald: "border-emerald-200/70 bg-white text-emerald-700",
        indigo: "border-indigo-200/70 bg-white text-indigo-700",
      };
  return (
    <div className={cn("rounded-xl border px-4 py-3.5 shadow-sm", tones[tone])}>
      <p
        className={cn(
          "text-[10.5px] font-bold uppercase tracking-wider",
          dark ? "text-white/60" : "text-slate-400"
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "mt-1.5 text-[22px] font-bold tracking-tight",
          dark ? "text-white" : "text-slate-900"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function FieldCard({ group, tone = "blue", index = 0 }) {
  const styles = {
    blue: { icon: "text-blue-600 bg-blue-50", chip: "border-blue-100 bg-blue-50/70 text-blue-900" },
    emerald: {
      icon: "text-emerald-600 bg-emerald-50",
      chip: "border-emerald-100 bg-emerald-50/70 text-emerald-900",
    },
  };
  const style = styles[tone] || styles.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.05, duration: 0.4 }}
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center gap-2">
        <div className={cn("flex h-6 w-6 items-center justify-center rounded-md", style.icon)}>
          <Check className="h-3.5 w-3.5" />
        </div>
        <p className="text-[12.5px] font-bold uppercase tracking-wider text-slate-900">
          {group.title}
        </p>
      </div>
      {group.endpoints && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {group.endpoints.map((ep) => (
            <span
              key={ep}
              className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[10.5px] font-semibold text-white"
            >
              {ep}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {group.fields.map((field) => (
          <span
            key={`${group.title}-${field}`}
            className={cn(
              "rounded-lg border px-2 py-1 text-[11px] font-semibold leading-none",
              style.chip
            )}
          >
            {field}
          </span>
        ))}
      </div>
    </motion.div>
  );
}

function FieldWall({ groups, tone = "blue", cols = 3 }) {
  return (
    <div className={cn("grid h-full gap-4", cols === 3 ? "grid-cols-3" : "grid-cols-2")}>
      {groups.map((group, i) => (
        <FieldCard key={group.title} group={group} tone={tone} index={i} />
      ))}
    </div>
  );
}

function EndpointRow({ row, index }) {
  const methodStyle = {
    GET: "bg-blue-500/15 text-blue-300 ring-blue-400/30",
    POST: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
    MCP: "bg-indigo-500/15 text-indigo-300 ring-indigo-400/30",
  };
  const stateStyle = {
    ok: { cls: "text-emerald-300", icon: Check },
    more: { cls: "text-blue-300", icon: PlugZap },
    included: { cls: "text-blue-300", icon: Check },
  };
  const s = stateStyle[row.state];
  const StateIcon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.35 + index * 0.06, duration: 0.4 }}
      className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2"
    >
      <span
        className={cn(
          "w-12 shrink-0 rounded-md px-1.5 py-0.5 text-center font-mono text-[10.5px] font-bold ring-1",
          methodStyle[row.method]
        )}
      >
        {row.method}
      </span>
      <span className="flex-1 truncate font-mono text-[13px] text-slate-200">{row.path}</span>
      <span className={cn("flex items-center gap-1.5 text-[11px] font-semibold", s.cls)}>
        <StateIcon className="h-3.5 w-3.5" />
        {row.tag}
      </span>
    </motion.div>
  );
}

function OpenSlide() {
  return (
    <SlideShell
      dark
      eyebrow="Now Open · Developer Program"
      eyebrowIcon={Code2}
      title="The Asha API is open to developers —"
      titleAccent="with security built in."
      subtitle="Get programmatic access to the dental operating system. Read across most of the data model, write to patients and appointments, and request more as you grow — every integration governed by HIPAA-grade security."
    >
      <div className="grid h-full grid-cols-[1fr_440px] gap-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="flex flex-col justify-center gap-5"
        >
          {/* Endpoint preview */}
          <div className="relative rounded-2xl border border-white/10 bg-slate-900/60 p-5 backdrop-blur-md">
            <div className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-r from-blue-500/15 via-indigo-500/15 to-blue-500/15 blur-lg" />
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <Terminal className="h-3.5 w-3.5" />
              <span>api.asha.health</span>
            </div>
            <div className="space-y-1.5">
              {endpointRows.map((row, i) => (
                <EndpointRow key={`${row.method}-${row.path}`} row={row} index={i} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <MetricPill dark label="Read scope" value="Most fields" tone="blue" />
            <MetricPill dark label="Write scope" value="Patients + Appts" tone="emerald" />
            <MetricPill dark label="Pricing" value="Quote-based" tone="blue" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="relative rounded-2xl border border-white/10 bg-slate-900/60 p-6 shadow-2xl backdrop-blur-md"
        >
          <div className="absolute -inset-0.5 -z-10 rounded-2xl bg-gradient-to-b from-blue-500/15 to-indigo-500/15 blur-md" />
          <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-[12px] font-semibold text-blue-300">
            <Sparkles className="h-3.5 w-3.5" />
            <span>What the program gives you</span>
          </div>
          <div className="space-y-4">
            {programPillars.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3"
              >
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
                  <Check className="h-3 w-3 text-emerald-300" />
                </div>
                <p className="text-[15px] leading-snug text-slate-200">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </SlideShell>
  );
}

function AccessModelSlide() {
  const columns = [
    {
      icon: DatabaseZap,
      eyebrow: "Read",
      title: "Most fields",
      tone: "blue",
      body: "Read access spans patients, scheduling, insurance, claims, clinical, and practice intelligence — most of the Asha data model.",
      chips: ["patients", "appointments", "insurance", "claims", "clinical", "practice"],
    },
    {
      icon: FileCheck2,
      eyebrow: "Write",
      title: "Patients & Appointments",
      tone: "emerald",
      body: "Create and update patients and appointments out of the box — the two highest-value, safest write surfaces.",
      chips: ["POST patients", "PATCH patients", "POST appointments", "PUT appointments"],
    },
    {
      icon: PlugZap,
      eyebrow: "On request",
      title: "More endpoints",
      tone: "indigo",
      body: "Need additional read or write endpoints? We accommodate more on request, scoped per integration after review.",
      chips: ["claims", "payments", "documents", "clinical", "ledger"],
    },
  ];
  const toneMap = {
    blue: { ring: "border-blue-200", icon: "bg-blue-50 text-blue-600", eye: "text-blue-600", chip: "border-blue-100 bg-blue-50/70 text-blue-900", bar: "from-blue-500 to-indigo-500" },
    emerald: { ring: "border-emerald-200", icon: "bg-emerald-50 text-emerald-600", eye: "text-emerald-600", chip: "border-emerald-100 bg-emerald-50/70 text-emerald-900", bar: "from-emerald-500 to-teal-500" },
    indigo: { ring: "border-indigo-200", icon: "bg-indigo-50 text-indigo-600", eye: "text-indigo-600", chip: "border-indigo-100 bg-indigo-50/70 text-indigo-900", bar: "from-indigo-500 to-blue-500" },
  };

  return (
    <SlideShell
      eyebrow="The Access Model"
      eyebrowIcon={ShieldCheck}
      title="Open access,"
      titleAccent="least-privilege by design."
      subtitle="Read is broad. Write is intentionally narrow. Everything beyond the defaults is available on request, scoped to your use case."
    >
      <div className="flex h-full flex-col gap-4">
        <div className="grid flex-1 grid-cols-3 gap-5">
          {columns.map((col, i) => {
            const t = toneMap[col.tone];
            const Icon = col.icon;
            return (
              <motion.div
                key={col.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.45 }}
                className={cn(
                  "relative flex flex-col overflow-hidden rounded-2xl border bg-white p-6 shadow-sm",
                  t.ring
                )}
              >
                <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", t.bar)} />
                <div className="mb-4 flex items-center gap-3">
                  <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl", t.icon)}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className={cn("text-[11px] font-bold uppercase tracking-wider", t.eye)}>
                      {col.eyebrow}
                    </p>
                    <h3 className="text-[21px] font-bold leading-tight text-slate-900">{col.title}</h3>
                  </div>
                </div>
                <p className="text-[14px] leading-snug text-slate-600">{col.body}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {col.chips.map((chip) => (
                    <span
                      key={chip}
                      className={cn("rounded-lg border px-2 py-1 text-[11px] font-semibold", t.chip)}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.45 }}
          className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3.5"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="text-[14px] font-medium text-slate-700">
            Access — <span className="font-bold text-slate-900">including MCP</span> — is granted through the
            Developer Program: apply, complete a security questionnaire with a 3rd-party HIPAA audit, then
            receive scoped access. Pricing is quote-based.
          </p>
        </motion.div>
      </div>
    </SlideShell>
  );
}

function ReadAccessSlide() {
  return (
    <SlideShell
      eyebrow="Read Access"
      eyebrowIcon={DatabaseZap}
      title="Read access to"
      titleAccent="most fields in the data model."
      subtitle="From day one, read endpoints expose patient, scheduling, insurance, claims, clinical, and practice-intelligence data across the dental operating system."
    >
      <div className="h-full">
        <FieldWall groups={readFieldGroups} tone="blue" />
      </div>
    </SlideShell>
  );
}

function WriteAccessSlide() {
  return (
    <SlideShell
      tone="emerald"
      eyebrow="Write Access"
      eyebrowIcon={FileCheck2}
      title="Write access is scoped to"
      titleAccent="Patients & Appointments."
      subtitle="The two highest-value, lowest-risk write surfaces are enabled by default — create and update patients and appointments programmatically."
    >
      <div className="grid h-full grid-cols-[300px_1fr] gap-5">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex flex-col gap-4"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <p className="text-[13px] font-bold uppercase tracking-wider text-slate-500">
              Why scoped?
            </p>
            <p className="mt-2 text-[14px] leading-snug text-slate-600">
              Narrow write scope protects patient safety and data integrity. We grant exactly what
              your workflow needs — nothing more.
            </p>
          </div>

          <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-5 shadow-sm">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-indigo-600 shadow-sm">
              <PlugZap className="h-5 w-5" />
            </div>
            <p className="text-[13px] font-bold uppercase tracking-wider text-indigo-600">
              More on request
            </p>
            <p className="mt-2 text-[14px] leading-snug text-slate-600">
              Additional write endpoints are available after a quick review:
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {moreOnRequest.map((item) => (
                <span
                  key={item}
                  className="rounded-lg border border-indigo-200/70 bg-white px-2 py-1 text-[11px] font-bold text-indigo-700"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        <FieldWall groups={writeScopedGroups} tone="emerald" cols={2} />
      </div>
    </SlideShell>
  );
}

function SecuritySlide() {
  return (
    <SlideShell
      eyebrow="Security Measures"
      eyebrowIcon={Lock}
      title="Open doesn't mean"
      titleAccent="unguarded."
      subtitle="Every integration runs under HIPAA-grade controls. Access is scoped, encrypted, rate-limited, fully logged, and revocable at any time."
    >
      <div className="grid h-full grid-cols-3 gap-4">
        {securityMeasures.map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + i * 0.06, duration: 0.4 }}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-blue-200 hover:shadow-md transition-all"
            >
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-[16px] font-bold leading-tight text-slate-900">{m.title}</h3>
              <p className="mt-1.5 text-[13px] leading-snug text-slate-600">{m.body}</p>
            </motion.div>
          );
        })}
      </div>
    </SlideShell>
  );
}

function SecurityControlsSlide() {
  return (
    <SlideShell
      eyebrow="Security Controls"
      eyebrowIcon={ClipboardCheck}
      title="The controls we expect —"
      titleAccent="and hold ourselves to."
      subtitle="A representative set of the controls a vendor passing a Drata or Vanta HIPAA program would have in place. This is not the complete list."
    >
      <div className="flex h-full flex-col gap-4">
        <div className="grid flex-1 grid-cols-3 content-start gap-x-4 gap-y-2.5">
          {securityControls.map((c, i) => (
            <motion.div
              key={c}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.02, duration: 0.3 }}
              className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm"
            >
              <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <Check className="h-3 w-3" strokeWidth={3} />
              </div>
              <span className="text-[12.5px] font-medium leading-snug text-slate-700">{c}</span>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.45 }}
          className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3.5"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
            <ScrollText className="h-5 w-5" />
          </div>
          <p className="text-[14px] font-medium text-slate-700">
            <span className="font-bold text-slate-900">This isn't the full list.</span> Fill out the
            developer application and we'll send our complete security controls matrix.
          </p>
        </motion.div>
      </div>
    </SlideShell>
  );
}

function GettingAccessSlide() {
  return (
    <SlideShell
      eyebrow="Application + Security Review"
      eyebrowIcon={ShieldCheck}
      title="Access is granted"
      titleAccent="through the program."
      subtitle="One application. The API — including MCP — is provisioned after a short security review. There's no separate MCP request, and pricing is quote-based."
    >
      <div className="grid h-full grid-cols-[400px_1fr] gap-6">
        {/* The requirement */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative flex flex-col justify-center overflow-hidden rounded-2xl border border-blue-200 bg-white p-7 shadow-[0_24px_60px_-30px_rgba(37,99,235,0.4)]"
        >
          <div className="pointer-events-none absolute -top-24 -right-24 h-60 w-60 rounded-full bg-blue-100/70 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-60 w-60 rounded-full bg-indigo-100/70 blur-[80px]" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-blue-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              Required in the questionnaire
            </span>
            <h3 className="mt-4 text-[26px] font-bold leading-tight text-slate-900">
              A 3rd-party audit certifying your tech stack is{" "}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                HIPAA compliant.
              </span>
            </h3>
            <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">
              As part of the security questionnaire, you provide an attestation from an independent
              auditor verifying your environment meets HIPAA requirements. No audit, no access.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["Independent auditor", "Covers your full stack", "Quote-based pricing"].map((c) => (
                <span
                  key={c}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* The path */}
        <div className="flex flex-col justify-center gap-3">
          {accessSteps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.45 }}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold text-white">
                      {i + 1}
                    </span>
                    <h4 className="text-[16px] font-bold text-slate-900">{step.title}</h4>
                  </div>
                  <p className="mt-1 text-[13px] leading-snug text-slate-600">{step.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </SlideShell>
  );
}

function ApplySlide() {
  return (
    <SlideShell
      eyebrow="Apply"
      eyebrowIcon={KeyRound}
      title="Apply to the"
      titleAccent="Developer API Program."
      subtitle="Tell us what you're building. Access is application-based and pricing is quote-based — no call required to get started."
    >
      <div className="grid h-full grid-cols-[1fr_380px] gap-6">
        {/* What to have ready */}
        <div className="flex flex-col justify-center gap-3">
          <p className="text-[12px] font-bold uppercase tracking-wider text-blue-600">
            What to have ready
          </p>
          {whatToHaveReady.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.text}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.08, duration: 0.45 }}
                className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-[15px] font-medium leading-snug text-slate-700">{item.text}</p>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.45 }}
            className="mt-1 flex items-start gap-3 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3.5"
          >
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
              <FileCheck2 className="h-4 w-4" />
            </div>
            <p className="text-[13.5px] leading-snug text-slate-700">
              <span className="font-bold text-slate-900">Pricing is quote-based.</span> We scope your
              access from the application and send a quote before you go live.
            </p>
          </motion.div>
        </div>

        {/* CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="relative flex flex-col justify-center overflow-hidden rounded-2xl border border-slate-800/40 bg-[#0B1120] p-7 text-white shadow-2xl"
        >
          <div className="pointer-events-none absolute -top-20 -right-20 h-60 w-60 rounded-full bg-blue-600/20 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-indigo-600/20 blur-[80px]" />
          <div className="relative">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20">
              <img src="/assets/alogo.png" alt="Asha" className="h-7 w-7 object-contain" />
            </div>
            <h3 className="text-[24px] font-bold leading-tight">Start your application</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-slate-300">
              A few questions about your use case, your stack, and your HIPAA posture.
            </p>
            <div className="mt-6 space-y-2.5">
              <Link
                to="/developer-api-application"
                className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3.5 text-[15px] font-bold text-slate-900 transition hover:bg-blue-50"
              >
                <span className="flex items-center gap-3">
                  <Code2 className="h-5 w-5 text-blue-600" />
                  Apply now
                </span>
                <ArrowRight className="h-5 w-5 text-blue-600" />
              </Link>
              <a
                href="mailto:onboarding@asha.health"
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[14px] font-semibold text-white transition hover:bg-white/10"
              >
                <Mail className="h-5 w-5 text-blue-300" />
                Questions? onboarding@asha.health
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </SlideShell>
  );
}

const slideComponents = [
  OpenSlide,
  AccessModelSlide,
  ReadAccessSlide,
  WriteAccessSlide,
  SecuritySlide,
  SecurityControlsSlide,
  GettingAccessSlide,
  ApplySlide,
];

const ACCESS_STORAGE_KEY = "asha_api_developer_program_deck_access";
const SESSION_STORAGE_KEY = "asha_api_developer_program_deck_session_id";

function ApiDeveloperProgramDeckPage() {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [query, setQuery] = useState("");
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const filteredSlides = useMemo(
    () =>
      slides
        .map((slide, index) => ({ ...slide, index }))
        .filter((slide) =>
          `${slide.title} ${slide.section}`.toLowerCase().includes(query.toLowerCase())
        ),
    [query]
  );

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      setScale(containerRef.current.offsetWidth / 1400);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const hasAccess = localStorage.getItem(ACCESS_STORAGE_KEY);
    const sessionId = getStoredContentSessionId(SESSION_STORAGE_KEY);
    if (hasAccess && sessionId) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    localStorage.setItem(ACCESS_STORAGE_KEY, "true");
    console.log("API developer program access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => {
      const next = api.selectedScrollSnap();
      setCurrent(next);
      setSearchParams({ slide: slides[next]?.slug || slides[0].slug }, { replace: true });
    };
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api, setSearchParams]);

  useEffect(() => {
    if (!api) return;
    const slideParam = searchParams.get("slide");
    if (!slideParam) return;
    const index = slides.findIndex((slide) => slide.slug === slideParam);
    if (index >= 0) {
      setTimeout(() => api.scrollTo(index), 80);
    }
  }, [api, searchParams]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!api) return;
      if (event.key === "ArrowRight") api.scrollNext();
      if (event.key === "ArrowLeft") api.scrollPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [api]);

  return (
    <div className="relative flex min-h-screen w-screen flex-col items-center overflow-x-hidden bg-neutral-950 p-3 text-white xl:h-screen xl:overflow-hidden xl:p-6">
      <AnimatePresence>
        {isLocked && (
          <EmailGate
            onAccessGranted={handleAccessGranted}
            sessionStorageKey={SESSION_STORAGE_KEY}
          />
        )}
      </AnimatePresence>

      <div className="mb-4 flex w-full max-w-[1800px] items-center justify-between gap-3">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search slides..."
            className="w-full rounded-md border border-white/10 bg-white/5 py-2.5 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-white/25 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <div className="flex items-center gap-3">
          <p className="hidden text-sm font-medium text-white/55 md:block">
            {current + 1} / {slides.length}
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                I have questions
              </Button>
            </DialogTrigger>
            <DialogContent className="overflow-hidden rounded-lg border-0 bg-white p-0 shadow-2xl sm:max-w-md">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold tracking-tight text-slate-950">
                  Talk through API access
                </DialogTitle>
                <DialogDescription className="text-base text-slate-500">
                  We can scope your endpoints, credentials, and MCP path.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 p-6 pt-2">
                <Button
                  className="h-auto justify-start rounded-md bg-slate-950 px-4 py-4 text-left text-white hover:bg-blue-700"
                  onClick={() =>
                    window.open(
                      "https://calendly.com/asha-health/dental-one-stop-shop",
                      "_blank"
                    )
                  }
                >
                  <CalendarDays className="h-5 w-5" />
                  Book a meeting
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start rounded-md px-4 py-4 text-left"
                  onClick={() => (window.location.href = "sms:+19146779846")}
                >
                  <Phone className="h-5 w-5" />
                  Text (914) 677-9846
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start rounded-md px-4 py-4 text-left"
                  onClick={() =>
                    (window.location.href = "mailto:onboarding@asha.health")
                  }
                >
                  <Mail className="h-5 w-5" />
                  onboarding@asha.health
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex w-full max-w-[1800px] flex-1 flex-col items-center justify-center gap-5 xl:min-h-0 xl:flex-row">
        <aside className="order-2 flex w-full flex-none flex-col xl:order-1 xl:h-full xl:w-[270px] xl:pb-7">
          <ScrollArea className="min-h-[180px] flex-1 pr-3 xl:min-h-0">
            <div className="space-y-1 border-l border-white/10 pl-4">
              {filteredSlides.map((slide) => (
                <button
                  key={slide.slug}
                  onClick={() => api?.scrollTo(slide.index)}
                  className={cn(
                    "group relative flex w-full flex-col py-2 pl-3 text-left transition",
                    current === slide.index ? "opacity-100" : "opacity-45 hover:opacity-80"
                  )}
                >
                  <span
                    className={cn(
                      "absolute -left-[17px] top-2 h-8 w-[3px] rounded-full transition",
                      current === slide.index ? "bg-blue-400" : "bg-transparent"
                    )}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-[0.13em] text-white/35">
                    {slide.section}
                  </span>
                  <span
                    className={cn(
                      "mt-0.5 truncate text-sm font-semibold",
                      current === slide.index ? "text-blue-300" : "text-white"
                    )}
                  >
                    {slide.title}
                  </span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </aside>

        <main className="order-1 w-full max-w-[1400px] flex-shrink-0 xl:order-2 xl:flex-1">
          <div className="aspect-video w-full">
            <div ref={containerRef} className="relative h-full w-full overflow-hidden">
              <div
                style={{
                  width: "1400px",
                  height: "787.5px",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  position: "absolute",
                  inset: 0,
                }}
              >
                <Carousel setApi={setApi} className="h-full w-full">
                  <div className="relative h-full w-full overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-white/10">
                    <CarouselContent className="ml-0 h-full">
                      {slideComponents.map((Slide, index) => (
                        <CarouselItem key={slides[index].slug} className="h-full pl-0">
                          <motion.div
                            className="h-full"
                            initial={{ opacity: 0.98 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Slide />
                          </motion.div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </div>
                </Carousel>
              </div>
            </div>
          </div>
        </main>
      </div>

      <button
        aria-label="Previous slide"
        onClick={() => api?.scrollPrev()}
        className="fixed bottom-5 left-5 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur transition hover:bg-white/20 md:flex"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <button
        aria-label="Next slide"
        onClick={() => api?.scrollNext()}
        className="fixed bottom-5 right-5 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur transition hover:bg-white/20 md:flex"
      >
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export default ApiDeveloperProgramDeckPage;
